import {
  CartFragment,
  CartLineItemFragment,
  CheckoutStateType,
} from '../../types'
import { GaCartFragment } from '../../types/gaTracking'

import { mergeDiscountsByName } from './helpers'
import { DiscountTypeCombination, type Discount } from './helpers/types'

export const getCheckoutAddress = (
  ctAddress?: CartFragment['shippingAddress'] | CartFragment['billingAddress']
) => {
  return {
    additional_address_info: ctAddress?.additionalAddressInfo ?? '',
    city: ctAddress?.city ?? '',
    country: ctAddress?.country ?? '',
    first_name: ctAddress?.firstName ?? '',
    id: ctAddress?.id ?? 'new',
    last_name: ctAddress?.lastName ?? '',
    phone_number: ctAddress?.phone ?? '',
    postcode: ctAddress?.postalCode ?? '',
    state: ctAddress?.state ?? '',
    street_name: ctAddress?.streetName ?? '',
    street_number: ctAddress?.streetNumber ?? '',
  }
}

export const convertToCTAddress = (
  address: CheckoutStateType['shipping_address']
) => {
  return {
    additionalAddressInfo:
      address.additional_address_info !== ''
        ? address.additional_address_info
        : `${address.first_name} ${address.last_name}`,
    city: address.city,
    country: address.country,
    firstName: address.first_name,
    lastName: address.last_name,
    phone: address.phone_number,
    postalCode: address.postcode,
    state: address.state,
    streetName: address.street_name,
    streetNumber: address.street_number,
  }
}

interface GetCartTotalsOptions {
  shippingCostPreview?: number
}

type GaCartLineItem = NonNullable<GaCartFragment['lineItems']>[number]

const getLineItemDiscountTotal = (item: GaCartLineItem) => {
  const discountedPricePerQuantityTotal =
    item.discountedPricePerQuantity?.reduce(
      (itemAccum: number, discountedEntry) => {
        const entryQuantity =
          typeof discountedEntry.quantity === 'number'
            ? discountedEntry.quantity
            : item.quantity

        const entryDiscount =
          discountedEntry.discountedPrice.includedDiscounts.reduce(
            (entryAccum: number, discount) => {
              const centAmount = discount.discountedAmount?.centAmount
              const discountAmount =
                typeof centAmount === 'number' ? centAmount * entryQuantity : 0

              return entryAccum + discountAmount
            },
            0
          )

        return itemAccum + entryDiscount
      },
      0
    ) ?? 0

  if (discountedPricePerQuantityTotal > 0) {
    return discountedPricePerQuantityTotal
  }

  const listPrice = item.price?.value?.centAmount ?? 0
  const discountedListPrice = item.price?.discounted?.value?.centAmount

  if (
    typeof discountedListPrice === 'number' &&
    discountedListPrice >= 0 &&
    listPrice > discountedListPrice
  ) {
    return (listPrice - discountedListPrice) * item.quantity
  }

  return 0
}

const getCartCustomFieldsRaw = (cart: GaCartFragment) =>
  (
    cart as GaCartFragment & {
      custom?: {
        customFieldsRaw?: Array<{ name?: string; value?: unknown }>
      }
    }
  )?.custom?.customFieldsRaw ?? []

const getCustomFieldNumber = (cart: GaCartFragment, name: string) => {
  const value = getCartCustomFieldsRaw(cart).find(
    (field) => field.name === name
  )?.value

  return typeof value === 'number' ? value : undefined
}

const getCustomFieldString = (cart: GaCartFragment, name: string) => {
  const value = getCartCustomFieldsRaw(cart).find(
    (field) => field.name === name
  )?.value

  return typeof value === 'string' ? value : undefined
}

const getDeliveryFeeInCents = (deliveryFeeRaw: number) =>
  Number.isInteger(deliveryFeeRaw)
    ? deliveryFeeRaw
    : Math.round(deliveryFeeRaw * 100)

export const getCartTaxBreakdown = (cart: GaCartFragment) => {
  const itemsTotalTax = cart?.taxedPrice?.totalTax?.centAmount
  const standardTax = getCustomFieldNumber(cart, 'standard-tax')
  const importTax = getCustomFieldNumber(cart, 'import-tax') ?? 0
  const deliveryFeeRaw = getCustomFieldNumber(cart, 'retail-delivery-fee') ?? 0
  const deliveryFee = getDeliveryFeeInCents(deliveryFeeRaw)
  const deliveryFeeName =
    getCustomFieldString(cart, 'retail-delivery-fee-name') ?? ''

  const missingDeliveryFeeInTaxTotal =
    typeof standardTax === 'number' &&
    typeof itemsTotalTax === 'number' &&
    deliveryFee > 0 &&
    standardTax + importTax === itemsTotalTax

  const totalTax =
    missingDeliveryFeeInTaxTotal && typeof itemsTotalTax === 'number'
      ? itemsTotalTax + deliveryFee
      : itemsTotalTax

  return {
    totalTax,
    standardTax:
      standardTax ??
      (typeof totalTax === 'number' ? totalTax - deliveryFee : 0),
    deliveryFee,
    deliveryFeeName,
  }
}

export const getCartTotals = (
  cart: GaCartFragment,
  options?: GetCartTotalsOptions
) => {
  // works only with tax not included in price
  // commercetools -> Settings -> Project settings -> Taxes -> Included in price (unchecked)
  const shippingCostPreview = options?.shippingCostPreview || 0

  const cartHasTaxedCalculated = !!cart?.taxedPrice

  const itemsTotalNet =
    cart?.lineItems?.reduce((acc, item) => {
      const itemPrice = item.price?.value?.centAmount * item.quantity
      return acc + itemPrice
    }, 0) || 0

  const shipping =
    cart?.shippingInfo?.price.centAmount || shippingCostPreview || 0
  const discountedShipping =
    cart?.shippingInfo?.discountedPrice?.value.centAmount

  const itemsTotalTax = cart?.taxedPrice?.totalTax?.centAmount
  const { totalTax } = getCartTaxBreakdown(cart)

  const itemsTotalDiscounts =
    cart?.lineItems?.reduce((acc, item) => {
      return acc + getLineItemDiscountTotal(item)
    }, 0) || 0

  const total = cartHasTaxedCalculated
    ? cart.taxedPrice?.totalGross.centAmount
    : cart?.totalPrice?.centAmount || 0

  const adjustedTax = totalTax
  const adjustedTotal =
    typeof itemsTotalTax === 'number' && typeof adjustedTax === 'number'
      ? total + (adjustedTax - itemsTotalTax)
      : total

  return {
    subtotal: itemsTotalNet,
    shipping,
    discountedShipping,
    tax: adjustedTax,
    discount: itemsTotalDiscounts,
    total: adjustedTotal + shippingCostPreview,
  }
}

export const getCartItemPrices = (item: CartLineItemFragment) => {
  // works only with tax not included in price
  // commercetools -> Settings -> Project settings -> Taxes -> Included in price (unchecked)
  const tax = item.taxedPrice?.totalTax?.centAmount

  const netPrice = item.price.value.centAmount
  const netSalePrice = item.price.discounted?.value.centAmount
  const total = item.totalPrice?.centAmount

  return {
    netPrice: netPrice,
    netSalePrice,
    tax,
    discount: netSalePrice ? netSalePrice - netPrice : 0,
    total,
  }
}

const hasBothDiscountTypes = (lineItem: CartLineItemFragment) => {
  const { discountedPricePerQuantity, price } = lineItem
  const { discounted } = price ?? {}
  const hasBothDiscounts =
    discountedPricePerQuantity.length !== 0 && discounted?.value != null
  return hasBothDiscounts
}

export const getCartDiscounts = (cart: CartFragment) => {
  //  Product Discounts
  const productDiscounts: Discount[] = []

  cart.lineItems?.forEach((lineItem) => {
    const { price, quantity } = lineItem
    const { discounted, value: priceValue } = price ?? {}
    const { discount, value: discountedValue } = discounted ?? {}
    const name =
      discount?.name ||
      (discount as unknown as { obj: { name: Record<string, string> } })?.obj
        ?.name?.['en-US']

    if (name) {
      if (
        cart.discountTypeCombination?.type === DiscountTypeCombination.bestDeal
      ) {
        if (!hasBothDiscountTypes(lineItem)) {
          productDiscounts.push({
            name,
            value:
              (priceValue?.centAmount - discountedValue?.centAmount) * quantity,
          })
        }
      } else {
        productDiscounts.push({
          name,
          value:
            (priceValue?.centAmount - discountedValue?.centAmount) * quantity,
        })
      }
    }
  })

  // Cart Discounts
  const cartDiscounts: Discount[] = []

  cart.lineItems?.forEach((lineItem) => {
    const { discountedPricePerQuantity } = lineItem

    discountedPricePerQuantity.forEach((lineItemDiscount) => {
      const { discountedPrice, quantity } = lineItemDiscount
      discountedPrice.includedDiscounts.forEach((includedDiscount) => {
        const name =
          includedDiscount.discount?.name ||
          (
            includedDiscount.discount as unknown as {
              obj: { name: Record<string, string> }
            }
          )?.obj?.name?.['en-US']
        const { centAmount } = includedDiscount.discountedAmount

        if (name) {
          cartDiscounts.push({
            name,
            value: centAmount * quantity,
          })
        }
      })
    })
  })

  // Shipping Discounts
  const shippingDiscounts: Discount[] = []

  const shippingDiscountedPrice = cart.shippingInfo?.discountedPrice

  if (shippingDiscountedPrice) {
    shippingDiscountedPrice.includedDiscounts.forEach((includedDiscount) => {
      const name =
        includedDiscount.discount?.name ||
        (
          includedDiscount.discount as unknown as {
            obj: { name: Record<string, string> }
          }
        )?.obj?.name?.['en-US']
      const { centAmount } = includedDiscount.discountedAmount

      if (name) {
        shippingDiscounts.push({
          name,
          value: centAmount,
          hide: !shippingDiscountedPrice.value.centAmount, // hides the shipping discount value if it's 100% (free shipping)
        })
      }
    })
  }

  const allDiscounts = mergeDiscountsByName([
    ...productDiscounts,
    ...cartDiscounts,
    ...shippingDiscounts,
  ])

  return {
    lineItemDiscounts: allDiscounts,
  }
}
